import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Award, Briefcase, RefreshCw, Crown, Flag, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getScoreboard, getCurrentChallenge } from "@/lib/api";

const RANK_STYLES = [
  { bg: "bg-hammerl-highlight", border: "border-hammerl-ink", label: "GOLD" },
  { bg: "bg-hammerl-mixed", border: "border-hammerl-ink", label: "SILBER" },
  { bg: "bg-orange-200", border: "border-hammerl-ink", label: "BRONZE" },
];

function relTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "gerade eben";
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("de-DE");
  } catch (e) {
    console.warn("[Scoreboard.relTime] Failed to parse timestamp:", e);
    return "—";
  }
}

export default function ScoreboardPage() {
  const [rows, setRows] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [data, ch] = await Promise.all([getScoreboard(), getCurrentChallenge()]);
      setRows(data.profiles);
      setChallenge(ch);
      setLastFetched(new Date());
    } catch (e) {
      console.error("[Scoreboard] Failed to load scoreboard/challenge:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-hammerl-paper text-hammerl-ink">
      <header className="border-b-2 border-hammerl-ink bg-hammerl-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider hover:bg-hammerl-highlight hover:text-hammerl-paper px-2 py-1 transition-colors text-hammerl-ink"
            data-testid="scoreboard-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Lobby
          </button>
          <div className="flex items-center gap-3">
            <img src="/brand/hammerl-logo.jpg" alt="Hobby Horsing Hammerl Logo" className="w-10 h-10 object-cover border border-hammerl-ink" />
            <div>
              <div className="font-bold text-xs sm:text-sm tracking-tight">Hobby Horsing Hammerl</div>
              <div className="text-[10px] sm:text-xs text-hammerl-pencil font-mono">Reiter-Performance · Live</div>
            </div>
          </div>
          <Button
            onClick={load}
            variant="ghost"
            className="rounded-none hover:bg-hammerl-highlight hover:text-hammerl-paper font-mono text-xs text-hammerl-ink"
            data-testid="scoreboard-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Aktualisieren
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-hammerl-pencil mb-2">Quartals-KPI // Reiter-Ranking</div>
          <h1 className="font-sans font-bold text-4xl sm:text-5xl tracking-tighter">
            Hammerl <span className="bg-hammerl-highlight px-2 -rotate-1 inline-block text-hammerl-paper">Charts</span>
          </h1>
          <p className="mt-2 text-base text-hammerl-pencil max-w-2xl">
            Marktführende Performance-Übersicht aller registrierten Hobby-Horsing-Athleten. Ungerino schickt die Excel-Datei jeden Montag an die Geschäftsführung.
          </p>
          {lastFetched && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-hammerl-pencil mt-3">
              Aktualisiert: {lastFetched.toLocaleTimeString("de-DE")} · Auto-Refresh alle 8s
            </div>
          )}
        </div>

        {/* Weekly Challenge Box */}
        {challenge && (
          <div className="bg-hammerl-card border-2 border-hammerl-ink shadow-brutal mb-6" data-testid="weekly-challenge-box">
            <div className="px-4 py-3 border-b-2 border-hammerl-ink bg-hammerl-corpblue text-white flex items-center justify-between">
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
                <Flag className="w-4 h-4" /> Wöchentliche Galopp-Challenge · {challenge.week}
              </div>
              <div className="font-mono text-xs">
                {challenge.completers} {challenge.completers === 1 ? "Reiter" : "Reiter"} erledigt
              </div>
            </div>
            <div className="p-4 grid md:grid-cols-[1fr_2fr] gap-4">
              <div>
                <div className="font-bold text-lg">{challenge.challenge.name}</div>
                <div className="text-sm text-hammerl-pencil mt-1">{challenge.challenge.description}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-hammerl-pencil mt-2">
                  Belohnung: +{challenge.challenge.reward_xp} XP
                </div>
              </div>
              <div className="border-l-0 md:border-l-2 md:pl-4 border-hammerl-lines">
                <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-2">Erfolgsliste</div>
                {challenge.leaderboard.length === 0 ? (
                  <div className="font-handwriting text-xl text-hammerl-pencil italic">Noch niemand. Galoppier los!</div>
                ) : (
                  <div className="space-y-1">
                    {challenge.leaderboard.slice(0, 8).map((entry, i) => (
                      <button
                        key={entry.id}
                        onClick={() => navigate(`/rider/${entry.id}`)}
                        className="w-full text-left flex items-center justify-between px-2 py-1 hover:bg-hammerl-highlight hover:text-hammerl-paper transition-colors text-hammerl-ink"
                        data-testid={`challenge-leaderboard-${entry.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-hammerl-pencil w-5 text-right">{i + 1}.</span>
                          <CheckCircle2 className="w-3.5 h-3.5 text-hammerl-corpblue shrink-0" />
                          <span className="font-bold text-sm truncate">{entry.name}</span>
                          <span className="font-mono text-xs text-hammerl-pencil">LVL {entry.level}</span>
                        </div>
                        <span className="font-mono text-[10px] text-hammerl-pencil hidden sm:inline">
                          {new Date(entry.completed_at).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-hammerl-card border-2 border-hammerl-ink shadow-brutal overflow-hidden">
          <div className="grid grid-cols-[48px_1fr_56px_72px] sm:grid-cols-[60px_1fr_70px_90px_90px_90px_110px] gap-2 sm:gap-4 border-b-2 border-hammerl-ink px-3 sm:px-4 py-3 bg-hammerl-sidebar font-mono text-[10px] sm:text-xs uppercase tracking-widest">
            <div>Rang</div>
            <div>Reiter</div>
            <div className="text-right">LVL</div>
            <div className="text-right sm:hidden">Ausz.</div>
            <div className="text-right hidden sm:block">XP</div>
            <div className="text-right hidden sm:block">Aktionen</div>
            <div className="text-right hidden sm:block">Items</div>
            <div className="text-right hidden sm:block">Auszeich.</div>
          </div>

          {loading && rows.length === 0 && (
            <div className="p-8 text-center font-mono text-sm text-hammerl-pencil">Lade KPIs…</div>
          )}

          {!loading && rows.length === 0 && (
            <div className="p-8 text-center font-handwriting text-2xl text-hammerl-pencil italic">
              Noch kein Reiter im Wettkampf-Pool. Hammerl ist enttäuscht.
            </div>
          )}

          {rows.map((r, idx) => {
            const rankStyle = RANK_STYLES[idx];
            return (
              <motion.div
                key={r.id}
                layout
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => navigate(`/rider/${r.id}`)}
                className="grid grid-cols-[48px_1fr_56px_72px] sm:grid-cols-[60px_1fr_70px_90px_90px_90px_110px] gap-2 sm:gap-4 px-3 sm:px-4 py-3 border-b border-hammerl-lines last:border-b-0 hover:bg-hammerl-highlight/20 cursor-pointer transition-colors items-center"
                data-testid={`scoreboard-row-${r.id}`}
              >
                <div className="flex items-center">
                  {rankStyle ? (
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 ${rankStyle.bg} text-hammerl-paper border-2 ${rankStyle.border} flex items-center justify-center font-mono font-bold text-sm`}>
                      {idx === 0 ? <Crown className="w-4 h-4" /> : idx + 1}
                    </div>
                  ) : (
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-hammerl-sidebar border border-hammerl-lines flex items-center justify-center font-mono text-sm text-hammerl-pencil">
                      {idx + 1}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm sm:text-base truncate">{r.name}</div>
                  <div className="text-[10px] font-mono text-hammerl-pencil truncate">
                    <span className="sm:hidden">XP {r.xp} · </span>{relTime(r.updated_at)}
                  </div>
                </div>
                <div className="text-right font-mono font-bold">{r.level}</div>
                <div className="text-right sm:hidden">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-card font-mono font-bold text-[10px]">
                    <Award className="w-3 h-3" /> {r.achievements_count}
                  </span>
                </div>
                <div className="text-right font-mono hidden sm:block">{r.xp}</div>
                <div className="text-right font-mono hidden sm:block">{r.actions_count}</div>
                <div className="text-right font-mono hidden sm:block">{r.inventory_count}</div>
                <div className="text-right hidden sm:block">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-hammerl-ink bg-hammerl-card font-mono font-bold text-xs">
                    <Award className="w-3 h-3" /> {r.achievements_count}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 font-handwriting text-xl text-hammerl-blue -rotate-1 inline-block">
          P.S.: Klick einen Reiter, um in seine Galopp-Akte zu schauen.
        </div>
      </main>
    </div>
  );
}
